import {
    Component,
    ElementRef,
    EventEmitter,
    forwardRef,
    HostListener,
    Input,
    Output,
    ViewChild,
    AfterViewInit,
    OnDestroy
  } from '@angular/core';
  import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
  import { CommonModule } from '@angular/common';
  import { MatIconModule } from '@angular/material/icon';
  import { MatButtonModule } from '@angular/material/button';
  import { MatTooltipModule } from '@angular/material/tooltip';
  import { MatMenuModule } from '@angular/material/menu';
import { CompanyUserDTO } from 'src/app/models/company/company-user-dto.model';
  
  interface MentionMatch {
    index: number;
    length: number;
    query: string;
  }
  
  @Component({
    selector: 'app-rich-text-editor',
    standalone: true,
    imports: [CommonModule, MatIconModule, MatButtonModule, MatTooltipModule, MatMenuModule],
    providers: [
      {
        provide: NG_VALUE_ACCESSOR,
        useExisting: forwardRef(() => RichTextEditorComponent),
        multi: true
      }
    ],
    templateUrl: './rich-text-editor.component.html',
    styleUrls: ['./rich-text-editor.component.scss']
  })
  export class RichTextEditorComponent implements ControlValueAccessor, AfterViewInit, OnDestroy {
    @ViewChild('editor', { static: false }) editorElement: ElementRef<HTMLDivElement>;
    @ViewChild('mentionMenu', { static: false }) mentionMenuElement: ElementRef<HTMLDivElement>;
  
    @Input() placeholder = 'Enter your notes here...';
    @Input() disabled = false;
    @Input() users: CompanyUserDTO[] = [];
    @Input() minHeight = '200px';
    @Input() showAttachButton = false;

    @Output() mentionUser = new EventEmitter<CompanyUserDTO>();
    @Output() attachFile = new EventEmitter<void>();
  
    public content = '';
    public showMentionMenu = false;
    public mentionQuery = '';
    public filteredUsers: CompanyUserDTO[] = [];
    public selectedUserIndex = 0;
    public mentionMenuPosition = { top: 0, left: 0 };
  
    private onChange: (value: string) => void = () => {};
    public onTouched: () => void = () => {};
    private mentionStartIndex = -1;
  
    ngAfterViewInit(): void {
      if (this.editorElement?.nativeElement && this.content) {
        this.editorElement.nativeElement.innerHTML = this.content;
      }
    }
  
    ngOnDestroy(): void {
      this.showMentionMenu = false;
    }
  
    writeValue(value: string): void {
      this.content = value || '';
      if (this.editorElement?.nativeElement) {
        this.editorElement.nativeElement.innerHTML = this.content;
      }
    }
  
    registerOnChange(fn: (value: string) => void): void {
      this.onChange = fn;
    }
  
    registerOnTouched(fn: () => void): void {
      this.onTouched = fn;
    }
  
    setDisabledState(isDisabled: boolean): void {
      this.disabled = isDisabled;
    }
  
    onInput(event: Event): void {
      const target = event.target as HTMLDivElement;
      this.content = target.innerHTML;
      this.onChange(this.content);
      this.checkForMention();
    }
  
    onPaste(event: ClipboardEvent): void {
      event.preventDefault();
      const text = event.clipboardData?.getData('text/plain');
      if (text) {
        document.execCommand('insertText', false, text);
      }
    }
  
    onKeyDown(event: KeyboardEvent): void {
      if (this.showMentionMenu) {
        if (event.key === 'ArrowDown') {
          event.preventDefault();
          this.selectedUserIndex = Math.min(
            this.selectedUserIndex + 1,
            this.filteredUsers.length - 1
          );
        } else if (event.key === 'ArrowUp') {
          event.preventDefault();
          this.selectedUserIndex = Math.max(this.selectedUserIndex - 1, 0);
        } else if (event.key === 'Enter' || event.key === 'Tab') {
          event.preventDefault();
          if (this.filteredUsers[this.selectedUserIndex]) {
            this.insertMention(this.filteredUsers[this.selectedUserIndex]);
          }
        } else if (event.key === 'Escape') {
          this.closeMentionMenu();
        }
      }
    }
  
    execCommand(command: string, value?: string): void {
      document.execCommand(command, false, value);
      this.editorElement.nativeElement.focus();
      this.updateContent();
    }
  
    insertLink(): void {
      const url = prompt('Enter URL:');
      if (url) {
        this.execCommand('createLink', url);
      }
    }
  
    insertList(ordered: boolean): void {
      this.execCommand(ordered ? 'insertOrderedList' : 'insertUnorderedList');
    }
  
    changeFontSize(size: string): void {
      this.execCommand('fontSize', size);
    }
  
    changeTextColor(): void {
      const color = prompt('Enter color (e.g., #000000):');
      if (color) {
        this.execCommand('foreColor', color);
      }
    }
  
    changeBackgroundColor(): void {
      const color = prompt('Enter background color (e.g., #ffff00):');
      if (color) {
        this.execCommand('backColor', color);
      }
    }
  
    private updateContent(): void {
      this.content = this.editorElement.nativeElement.innerHTML;
      this.onChange(this.content);
    }
  
    private checkForMention(): void {
      const selection = window.getSelection();
      if (!selection || selection.rangeCount === 0) {
        this.closeMentionMenu();
        return;
      }
  
      const range = selection.getRangeAt(0);
      const textContent = this.editorElement.nativeElement.textContent || '';
      const cursorPosition = this.getCursorPosition();
  
      // Look backwards from cursor for @ symbol
      let mentionStart = -1;
      for (let i = cursorPosition - 1; i >= 0; i--) {
        if (textContent[i] === '@') {
          mentionStart = i;
          break;
        }
        if (textContent[i] === ' ' || textContent[i] === '\n') {
          break;
        }
      }
  
      if (mentionStart >= 0) {
        const query = textContent.substring(mentionStart + 1, cursorPosition).toLowerCase();
        this.mentionQuery = query;
        this.mentionStartIndex = mentionStart;
        this.filterUsers(query);
        this.showMentionMenu = this.filteredUsers.length > 0;
        
        if (this.showMentionMenu) {
          this.positionMentionMenu(range);
        }
      } else {
        this.closeMentionMenu();
      }
    }
  
    private getCursorPosition(): number {
      const selection = window.getSelection();
      if (!selection || selection.rangeCount === 0) return 0;
  
      const range = selection.getRangeAt(0);
      const preCaretRange = range.cloneRange();
      preCaretRange.selectNodeContents(this.editorElement.nativeElement);
      preCaretRange.setEnd(range.endContainer, range.endOffset);
      
      return preCaretRange.toString().length;
    }
  
    private filterUsers(query: string): void {
      if (!query) {
        this.filteredUsers = this.users.slice(0, 5);
      } else {
        this.filteredUsers = this.users
          .filter(user => {
            const fullName = `${user.firstName} ${user.lastName}`.toLowerCase();
            return fullName.includes(query);
          })
          .slice(0, 5);
      }
      this.selectedUserIndex = 0;
    }
  
    private positionMentionMenu(range: Range): void {
      const rangeRect = range.getBoundingClientRect();
      const menuHeight = 300; // approximate max height of mention menu
      const viewportHeight = window.innerHeight;

      // If not enough space below, position above the cursor
      if (rangeRect.bottom + menuHeight > viewportHeight) {
        this.mentionMenuPosition = {
          top: rangeRect.top - menuHeight - 5,
          left: rangeRect.left
        };
      } else {
        this.mentionMenuPosition = {
          top: rangeRect.bottom + 5,
          left: rangeRect.left
        };
      }
    }
  
    selectUser(user: CompanyUserDTO, index: number): void {
      this.selectedUserIndex = index;
      this.insertMention(user);
    }
  
    private insertMention(user: CompanyUserDTO): void {
      const selection = window.getSelection();
      if (!selection || this.mentionStartIndex < 0) {
        this.closeMentionMenu();
        return;
      }
  
      // Get all text nodes and find the one containing the @ symbol
      const textContent = this.editorElement.nativeElement.textContent || '';
      const cursorPosition = this.getCursorPosition();
      
      // Create mention span
      const mentionSpan = document.createElement('span');
      mentionSpan.className = 'mention';
      mentionSpan.contentEditable = 'false';
      mentionSpan.setAttribute('data-user-id', user.azureObjectId.toString());
      mentionSpan.textContent = `@${user.firstName} ${user.lastName}`;
  
      // Find the text node containing the @ and replace the mention text
      const walker = document.createTreeWalker(
        this.editorElement.nativeElement,
        NodeFilter.SHOW_TEXT,
        null
      );
  
      let currentPos = 0;
      let targetNode: Node | null = null;
      let nodeStartPos = 0;
  
      while (walker.nextNode()) {
        const node = walker.currentNode;
        const nodeLength = node.textContent?.length || 0;
        
        if (currentPos <= this.mentionStartIndex && currentPos + nodeLength > this.mentionStartIndex) {
          targetNode = node;
          nodeStartPos = currentPos;
          break;
        }
        currentPos += nodeLength;
      }
  
      if (targetNode && targetNode.textContent) {
        const nodeOffset = this.mentionStartIndex - nodeStartPos;
        const mentionLength = cursorPosition - this.mentionStartIndex;
        
        const beforeText = targetNode.textContent.substring(0, nodeOffset);
        const afterText = targetNode.textContent.substring(nodeOffset + mentionLength);
        
        const beforeNode = document.createTextNode(beforeText);
        const afterNode = document.createTextNode(' ' + afterText);
        
        const parent = targetNode.parentNode;
        if (parent) {
          parent.insertBefore(beforeNode, targetNode);
          parent.insertBefore(mentionSpan, targetNode);
          parent.insertBefore(afterNode, targetNode);
          parent.removeChild(targetNode);
          
          // Set cursor after mention
          const range = document.createRange();
          range.setStartAfter(mentionSpan);
          range.collapse(true);
          selection.removeAllRanges();
          selection.addRange(range);
        }
      }
  
      this.mentionUser.emit(user);
      this.updateContent();
      this.closeMentionMenu();
    }
  
    private closeMentionMenu(): void {
      this.showMentionMenu = false;
      this.mentionQuery = '';
      this.mentionStartIndex = -1;
      this.selectedUserIndex = 0;
    }
  
    @HostListener('document:click', ['$event'])
    onDocumentClick(event: MouseEvent): void {
      if (this.showMentionMenu && 
          this.mentionMenuElement && 
          !this.mentionMenuElement.nativeElement.contains(event.target as Node) &&
          !this.editorElement.nativeElement.contains(event.target as Node)) {
        this.closeMentionMenu();
      }
    }
  }